/**
 * valueTransforms.js
 *
 * Design-time value transform pipeline — a small, strategy-agnostic
 * utility. A transform takes one value and produces one value; a
 * pipeline is an ordered list of transforms applied in sequence. This
 * has no knowledge of 'state'/'mapping'/'template'/'constant' at all —
 * any strategy's preview could run a pipeline before its own evaluation,
 * which is what makes it usable by all of them rather than tied to one.
 *
 * Editor-only, exactly like evaluateMappingRules.js/interpolateColor.js:
 * no generator, migration, or AutomationBinding involvement. Generator
 * support (actually emitting Jinja2 filters for these) is an explicitly
 * later, separate feature — see the "Feature 5 — Value Transforms" brief.
 * Currently only MappingBindingEditor.vue/BindingPreview.vue wire this
 * in, since that's the only editor with a preview surface today; nothing
 * here prevents another editor from reusing it later.
 *
 * @typedef {{ type: string, params: Object<string, *> }} TransformStep
 */

/**
 * Metadata for every supported transform type — used by TransformEditor.vue
 * to render the type picker and each step's parameter inputs, and by
 * validateTransformPipeline.js to check parameter validity and sequencing.
 * `numeric: true` on the transform means it operates on a number (its
 * input must already have been converted, e.g. via 'integer'/'float');
 * `numeric: true` on a param means that param itself must be a number.
 */
export const TRANSFORM_TYPES = {
  integer:   { label: 'Integer',        numeric: true,  converts: true, params: [] },
  float:     { label: 'Float',          numeric: true,  converts: true, params: [] },
  round:     { label: 'Round',          numeric: true,  params: [{ key: 'decimals', label: 'Decimals', numeric: true, default: 0 }] },
  abs:       { label: 'Absolute Value', numeric: true,  params: [] },
  clamp:     { label: 'Clamp',          numeric: true,  params: [{ key: 'min', label: 'Min', numeric: true, default: 0 }, { key: 'max', label: 'Max', numeric: true, default: 100 }] },
  multiply:  { label: 'Multiply',       numeric: true,  params: [{ key: 'factor', label: 'Factor', numeric: true, default: 1 }] },
  divide:    { label: 'Divide',         numeric: true,  params: [{ key: 'divisor', label: 'Divisor', numeric: true, default: 1 }] },
  add:       { label: 'Add',            numeric: true,  params: [{ key: 'amount', label: 'Amount', numeric: true, default: 0 }] },
  subtract:  { label: 'Subtract',       numeric: true,  params: [{ key: 'amount', label: 'Amount', numeric: true, default: 0 }] },
  prefix:    { label: 'Prefix',         numeric: false, params: [{ key: 'text', label: 'Text', numeric: false, default: '' }] },
  suffix:    { label: 'Suffix',         numeric: false, params: [{ key: 'text', label: 'Text', numeric: false, default: '' }] },
  uppercase: { label: 'Uppercase',      numeric: false, params: [] },
  lowercase: { label: 'Lowercase',      numeric: false, params: [] },
  trim:      { label: 'Trim',           numeric: false, params: [] },
};

/** @private */
function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** @private */
function roundTo(value, decimals) {
  const factor = 10 ** Math.max(0, Math.trunc(decimals));
  return Math.round(value * factor) / factor;
}

/** @private */
function applyTransform(step, value) {
  const p = step.params ?? {};
  switch (step.type) {
    case 'integer':   return String(Math.trunc(toNumber(value)));
    case 'float':     return String(toNumber(value));
    case 'round':     return String(roundTo(toNumber(value), toNumber(p.decimals)));
    case 'abs':       return String(Math.abs(toNumber(value)));
    case 'clamp':     return String(Math.min(toNumber(p.max), Math.max(toNumber(p.min), toNumber(value))));
    case 'multiply':  return String(toNumber(value) * toNumber(p.factor));
    case 'divide':    return String(toNumber(value) / toNumber(p.divisor));
    case 'add':       return String(toNumber(value) + toNumber(p.amount));
    case 'subtract':  return String(toNumber(value) - toNumber(p.amount));
    case 'prefix':    return `${p.text ?? ''}${value}`;
    case 'suffix':    return `${value}${p.text ?? ''}`;
    case 'uppercase': return String(value).toUpperCase();
    case 'lowercase': return String(value).toLowerCase();
    case 'trim':      return String(value).trim();
    default:          return value;
  }
}

/**
 * Default parameter values for a transform type — used when adding a new
 * step or when a step's type changes.
 * @param {string} type
 * @returns {Object<string, *>}
 */
export function defaultParamsFor(type) {
  const meta = TRANSFORM_TYPES[type];
  return Object.fromEntries((meta?.params ?? []).map(p => [p.key, p.default]));
}

/**
 * Run an ordered transform pipeline over one incoming value.
 *
 * @param {TransformStep[]} [pipeline]
 * @param {string} value
 * @returns {string}
 */
export function runTransformPipeline(pipeline, value) {
  return (pipeline ?? []).reduce((current, step) => applyTransform(step, current), value);
}
